/**
 * Lock forms as the payoff of a read (duel v3 P3; user, 2026-10-11: a lock form locks FULLY only on an OPENED foe). In a bout's
 * fight (`DUEL_FLAGS.v2`); the open world keeps systems/duelLock.ts as it is. Research: docs/research/DUEL_LOCK_FORMS.md.
 *
 * - The read: a lock move's first blow is judged on the foe as it was just before the blow. **Opened** (staggered or stunned,
 *   guard broken, in a move's recovery or a step's stop) -> the full form (and, for a move with a routine in
 *   data/lockRoutines.ts, the long routine). **Guarding** -> no form: the guard takes it as usual (cut, guard points drained,
 *   the shove) and the caster is left in a stop (`DUEL_LOCK_OPEN.guardedStop`). **Neutral** -> a plain hit, no hold.
 * - The routine: its beats' blows land on time (`h.hit`, through the bout's rules), the caster replays its swing on each blow
 *   (the client's placeholder until the beat poses exist) and keeps close to the held foe. Nothing breaks a sealed routine.
 * - ดิ้นหลุด (escape), once a round: guard + dodge together on the white flash at the end of the catch cuts the form; it costs
 *   `DUEL_LOCK_OPEN.escapeGp` guard points (a broken guard has none to pay: break -> lock is sure), the escaper steps back
 *   with i-frames, the caster is left in a stop. A dodge pressed before the flash spends the chance for that form. No damage
 *   back, no counter blow (the user's guard rule).
 * Deterministic: time from the hooks' dt, no randomness here.
 */
import { DUEL_FLAGS } from '../data/duelMatch';
import { beatAt, DUEL_LOCK_OPEN, lockRoutineOf } from '../data/lockRoutines';
import { COMBAT, type HitResult } from '../combat';
import { SKILLS, type SkillDef, type SkillKind } from '../data/skills';
import type { Entity, PlayerCommand, WorldEvent } from '../world';
import { duelArmorOf } from './duelArmor';
import { duelLockOf, setFormGate } from './duelLock';
import { duelMatchOf } from './duelMatch';
import { duelRecoveryOf } from './duelRecovery';
import { longMoveSysOf } from './longMove';
import { skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelLockOpenSystem>();
export const duelLockOpenOf = (w: object): DuelLockOpenSystem | null => SYSTEMS.get(w) ?? null;

/** How a lock move's first blow was read. */
export type LockVerdict = 'full' | 'guarded' | 'plain';

/** A fighter is opened: staggered or stunned, guard broken, or in a recovery or stop. Guarding is never opened. */
export function isOpened(w: object, e: Entity): boolean {
  if (duelArmorOf(w)?.tier(e.id) === 'yellow') return true; // a yellow body is grabbed (DUEL-ARMOR)
  if (e.blocking) return false;
  return e.stunT > 0 || (duelMatchOf(w)?.brokenLeft(e.id) ?? 0) > 0 || (duelRecoveryOf(w)?.left(e.id) ?? 0) > 0;
}

/** The catch of a form (the escape window's end, seconds): a routine's own, else a short form's first third (≤ 0.4 s). */
export function catchOf(skillId: string, len: number): number {
  return lockRoutineOf(skillId)?.r.catch ?? Math.min(0.4, len * 0.3);
}
/** The white flash: the last 0.3 s of the catch (and a hair after). */
export const flashOf = (skillId: string, len: number): [number, number] => {
  const c = catchOf(skillId, len);
  return [Math.max(0, c - 0.3), c + 0.05];
};

interface Read {
  tgt: number;
  skillId: string;
  verdict: LockVerdict;
  /** The verdict has been acted on (the form started, the plain hit counted, the stop given): later strikes ride on it. */
  used: boolean;
}

export class DuelLockOpenSystem implements WorldSystem {
  readonly name = 'duelLockOpen';
  /** Caster -> the read of its lock move under way (from the first blow until its next move). */
  private readonly reads = new Map<number, Read>();
  /** Fighter -> the round its escape was spent in. */
  private readonly escaped = new Map<number, number>();
  /** Held fighter -> the form whose chance it spent (pressed early, or tried). */
  private readonly spent = new Map<number, number>();
  /** Player -> world time of the latest guard press, the latest dodge press, and whether the guard key is held. */
  private readonly keys = new Map<number, { block: number; dash: number; held: boolean }>();
  /** The latest escape (tests, the picture). */
  lastEscape: { id: number; caster: number; at: number } | null = null;
  /** Every read so far (tests, the sim). */
  readonly tally = { full: 0, guarded: 0, plain: 0 };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    const w = this.h.world;
    setFormGate(w, (c, t, s) => this.gate(c, t, s));
    // The bout's hit rules (duelMatch first, then this, then the slide's): the read is made on the foe as it was before.
    const prev = w.duelMod;
    w.duelMod = (src, tgt, res, kind, skillId) => {
      this.read(src, tgt, res, skillId);
      prev?.(src, tgt, res, kind, skillId);
      this.after(src, res, kind, skillId);
    };
  }

  private inFight(id?: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (id === undefined || b.a === id || b.b === id);
  }

  /** How a caster's current lock move was read (null: none under way). */
  verdictOf(casterId: number): LockVerdict | null {
    return this.reads.get(casterId)?.verdict ?? null;
  }

  /** Whether a fighter may still break out this round. */
  canEscape(id: number): boolean {
    const st = duelMatchOf(this.h.world)?.current;
    return !st || this.escaped.get(id) !== st.round;
  }

  /**
   * For the renderer: the form a fighter is in, with the routine's beat, the catch and the white flash (seconds from the
   * start), and whether this one may still break out.
   */
  formView(id: number): {
    caster: number;
    target: number;
    skillId: string;
    t: number;
    len: number;
    beat: number;
    k: number;
    flash: [number, number];
    escapable: boolean;
  } | null {
    const lock = duelLockOf(this.h.world);
    const f = lock?.formOf(id);
    if (!lock || !f) return null;
    const info = lock.formInfo(f[0]);
    if (!info) return null;
    const rt = lockRoutineOf(info.skillId);
    const b = rt ? beatAt(rt.r, info.t) : { i: 0, k: info.t / info.len };
    return {
      caster: f[0],
      target: f[1],
      skillId: info.skillId,
      t: info.t,
      len: info.len,
      beat: b.i,
      k: b.k,
      flash: flashOf(info.skillId, info.len),
      escapable: this.canEscape(f[1]) && this.spent.get(f[1]) !== f[0],
    };
  }

  /** The foe's next ready lock move and its reach (for the reach ring): null when it has none ready. */
  readyLockOf(id: number): { skillId: string; range: number; opened: boolean } | null {
    const w = this.h.world;
    const e = w.get(id);
    if (!e || e.dead) return null;
    const p = w.players.get(id)?.progress;
    if (!p) return null;
    const st = duelMatchOf(w)?.current;
    const foeId = st ? (st.meId === id ? st.foeId : st.meId) : -1;
    const foe = w.get(foeId);
    for (const sid of skillBarOf(p)) {
      const s = sid ? SKILLS[sid] : undefined;
      if (!s?.lockForm || (e.cooldowns[s.id] ?? 0) > 0 || e.qi < s.qi) continue;
      return { skillId: s.id, range: s.range || s.aoe || 0, opened: !!foe && isOpened(w, foe) };
    }
    return null;
  }

  /** The read, before the bout's rules touch the blow. */
  private read(src: Entity, tgt: Entity, res: HitResult, skillId: string): void {
    const s = SKILLS[skillId];
    if (!s?.lockForm || res.outcome === 'dodge' || duelLockOf(this.h.world)?.inForm(src.id)) return;
    const r = this.reads.get(src.id);
    if (r && r.skillId === skillId && r.tgt === tgt.id) return; // its later strikes keep the first one's read
    // A red (armoured) body: the lock whiffs - a plain blow at most, and the caster is left in a stop (DUEL-ARMOR).
    if (duelArmorOf(this.h.world)?.tier(tgt.id) === 'red') {
      this.reads.set(src.id, { tgt: tgt.id, skillId, verdict: 'plain', used: false });
      duelRecoveryOf(this.h.world)?.startStop(src.id, DUEL_LOCK_OPEN.redStop, 'lock-red');
      return;
    }
    const verdict: LockVerdict = tgt.blocking ? 'guarded' : isOpened(this.h.world, tgt) ? 'full' : 'plain';
    this.reads.set(src.id, { tgt: tgt.id, skillId, verdict, used: false });
  }

  /** After the bout's rules: a lock move guarded leaves its caster in a stop. */
  private after(src: Entity, res: HitResult, kind: SkillKind, skillId: string): void {
    void kind;
    const r = this.reads.get(src.id);
    if (!r || r.used || r.skillId !== skillId || r.verdict !== 'guarded' || res.outcome !== 'blocked') return;
    const rec = duelRecoveryOf(this.h.world);
    rec?.startStop(src.id, Math.max(rec.left(src.id), DUEL_LOCK_OPEN.guardedStop), 'lock-guarded');
    r.used = true; // once per use
    this.tally.guarded++;
  }

  /** duelLock asks: may this blow start its form? Only on the read `full` (outside a bout fight: as before). */
  private gate(c: Entity, t: Entity, s: SkillDef): boolean {
    if (!this.inFight(c.id)) return true;
    const r = this.reads.get(c.id);
    if (!r || r.skillId !== s.id || r.tgt !== t.id) return true;
    if (r.used || r.verdict !== 'full') {
      if (r.verdict === 'plain' && !r.used) this.tally.plain++;
      r.used = true;
      return false;
    }
    r.used = true; // once per use: the form starts now
    this.tally.full++;
    if (lockRoutineOf(s.id)) longMoveSysOf(this.h.world)?.startLock(c.id, t.id, s.id);
    this.spent.delete(t.id);
    return true;
  }

  refuse(cmd: PlayerCommand): boolean {
    const w = this.h.world;
    const p = w.player;
    if (!this.inFight(p.id)) return false;
    const k = this.keys.get(p.id) ?? { block: -9, dash: -9, held: false };
    this.keys.set(p.id, k);
    if (cmd.t === 'block') {
      k.held = cmd.on;
      if (cmd.on) {
        k.block = w.time;
        if (w.time - k.dash <= DUEL_LOCK_OPEN.pairWindow) this.tryEscape(p.id, true);
      }
      return false; // a held fighter's guard is dropped by duelLock as before
    }
    if (cmd.t === 'dash') {
      const lock = duelLockOf(w);
      const f = lock?.formOf(p.id);
      if (!f || f[1] !== p.id) return false;
      k.dash = w.time;
      if (k.held || w.time - k.block <= DUEL_LOCK_OPEN.pairWindow) this.tryEscape(p.id, true);
      else this.early(p.id);
      return true;
    }
    return false;
  }

  /** A dodge pressed before the flash (and not with the guard) spends the chance for this form. */
  private early(id: number): void {
    const v = this.formView(id);
    if (v && v.t < v.flash[0]) this.spent.set(id, v.caster);
  }

  /** ดิ้นหลุด: true when the held fighter broke out. `tell` says why not to a player. */
  tryEscape(id: number, tell = false): boolean {
    const w = this.h.world;
    const v = this.formView(id);
    if (!v || v.target !== id) return false;
    const say = (s: string) => {
      if (tell && id === w.player.id) this.h.notice(s);
    };
    if (!v.escapable) {
      if (!this.canEscape(id)) say('ดิ้นหลุดไปแล้วในยกนี้');
      return false;
    }
    if (v.t < v.flash[0] || v.t > v.flash[1]) {
      this.spent.set(id, v.caster);
      return false;
    }
    const d = duelMatchOf(w);
    if (!d?.spendGuard(id, DUEL_LOCK_OPEN.escapeGp)) {
      this.spent.set(id, v.caster);
      say('การ์ดไม่พอดิ้นหลุด');
      return false;
    }
    const st = d.current;
    if (st) this.escaped.set(id, st.round);
    const e = w.get(id)!;
    const c = w.get(v.caster);
    duelLockOf(w)?.cut(v.caster);
    longMoveSysOf(w)?.cancelLock(v.caster);
    // A step back with i-frames, away from the caster (as the combo escape's).
    let ux = c ? e.x - c.x : -e.dirX;
    let uy = c ? e.y - c.y : -e.dirY;
    const l = Math.hypot(ux, uy) || 1;
    ux /= l;
    uy /= l;
    Object.assign(e, {
      stunT: 0,
      hitT: 0,
      blocking: false,
      path: null,
      dashT: COMBAT.dashDuration,
      dashVX: ux * (DUEL_LOCK_OPEN.escapeDist / COMBAT.dashDuration),
      dashVY: uy * (DUEL_LOCK_OPEN.escapeDist / COMBAT.dashDuration),
      invulnT: COMBAT.dashDuration + 0.05,
    });
    if (c) duelRecoveryOf(w)?.startStop(c.id, DUEL_LOCK_OPEN.escapeStop, 'lock-escaped');
    this.lastEscape = { id, caster: v.caster, at: w.time };
    this.h.emit({ type: 'dash', id });
    return true;
  }

  observe(e: WorldEvent): void {
    // A new move ends the old one's read.
    if (e.type === 'skill' && this.reads.has(e.srcId) && !duelLockOf(this.h.world)?.inForm(e.srcId)) this.reads.delete(e.srcId);
  }

  step(dt: number): void {
    const w = this.h.world;
    if (!this.inFight()) {
      this.reads.clear();
      longMoveSysOf(w)?.clearLocks();
      this.spent.clear();
      if (!duelMatchOf(w)?.current) this.escaped.clear();
      return;
    }
    longMoveSysOf(w)?.stepLocks(dt); // the routines run in the long-move engine, at this same point of the step
  }
}
